import { cn } from '@/shared/lib/utils';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as React from 'react';

/**
 * RDS Radio.
 * @see https://www.figma.com/design/wcgji99mZRPIytCZDjwqxe/RDS-v1.0?node-id=80-1692
 */
export const radioItemClassName = cn(
  'aspect-square size-6 shrink-0 rounded-full border-[1.5px] border-[#9ea2ae] bg-white',
  'shadow-none transition-[background-color,border-color,box-shadow]',
  'hover:bg-ruda-primary-100',
  'focus:outline-none focus-visible:shadow-[0_0_0_3px_#aeb6fb]',
  'disabled:cursor-not-allowed disabled:border-[#d2d5db] disabled:bg-[#e5e7ea]',
  'disabled:hover:bg-[#e5e7ea]',
  // Selected — Primary-600 보더 + 내부 도트
  'data-[state=checked]:border-ruda-primary-600 data-[state=checked]:bg-white',
  'data-[state=checked]:hover:bg-white',
);

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    className={cn('grid gap-4', className)}
    {...props}
    ref={ref}
  />
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(radioItemClassName, className)}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      {/* Figma: 12px 도트, 외곽 24px 기준 inset 4.5px */}
      <span className="size-3 rounded-full bg-ruda-primary-600" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

/**
 * 버튼형 라디오.
 * 도트 대신 버튼 모양으로 보여줄 때 쓴다. 키보드 이동·선택은 Radix RadioGroup 그대로다.
 */
export const radioButtonItemClassName = cn(
  'flex h-10 w-full items-center justify-center rounded-md border px-3',
  'border-[#d2d5db] bg-white text-sm text-ruda-text-primary',
  'transition-[background-color,border-color,color,box-shadow]',
  'hover:bg-ruda-primary-50',
  'focus:outline-none focus-visible:shadow-[0_0_0_3px_#aeb6fb]',
  'disabled:cursor-not-allowed disabled:border-[#d2d5db] disabled:bg-[#e5e7ea]',
  'disabled:hover:bg-[#e5e7ea]',
  // Selected — Primary 보더 + 연한 배경
  'data-[state=checked]:border-ruda-primary-600 data-[state=checked]:bg-ruda-primary-50',
  'data-[state=checked]:font-medium data-[state=checked]:text-ruda-primary-700',
);

const RadioGroupButtonItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(radioButtonItemClassName, className)}
    {...props}
  >
    {children}
  </RadioGroupPrimitive.Item>
));
RadioGroupButtonItem.displayName = 'RadioGroupButtonItem';

export { RadioGroup, RadioGroupItem, RadioGroupButtonItem };
